import { useCallback, useEffect, useRef } from 'react';
import { NavigateOptions, To, useLocation, useNavigate } from 'react-router-dom';
import { isNullish } from 'remeda';
import { useSelectedPartner } from 'src/graphql';
import { getPartnerFriendlyIdentifier } from 'src/routes/hooks';

/**
 * wraps React‑Router's `navigate` to automatically prefix
 * every path with the current `selectedPartnerId` (if any)
 */
export const useNavigateRoute = () => {
  const { pathname } = useLocation();
  const nativeNavigate = useNavigate();
  const { selectedPartner, loading } = useSelectedPartner();

  const selectedPartnerFriendlyId = getPartnerFriendlyIdentifier(selectedPartner);

  // ref to hold a pending navigation
  const pendingNav = useRef<{ to: To; options?: NavigateOptions } | null>(null);

  // a lower-level function that *always* runs immediately,
  const applyPrefixAndNavigate = useCallback(
    (to: To, options?: NavigateOptions) => {
      // if it's a delta (number), bypass prefixing entirely
      if (typeof to === 'number') {
        return nativeNavigate(to);
      }

      // if there's no partner, just pass through string/object navigation
      if (isNullish(selectedPartnerFriendlyId)) {
        return nativeNavigate(to, options);
      }

      const prefix = `/${selectedPartnerFriendlyId}`;

      if (typeof to === 'string') {
        // string-based navigation
        const path = trimDuplicatedSlashes(`${prefix}/${to}`);
        return nativeNavigate(path, options);
      }

      // object-based navigation
      const pathname = to.pathname ?? '';
      const normalized = trimDuplicatedSlashes(`${prefix}/${pathname}`);
      return nativeNavigate({ ...to, pathname: normalized }, options);
    },
    [selectedPartnerFriendlyId, nativeNavigate],
  );

  // the public navigate() we expose
  // if loading - stashes the call; otherwise do it now.
  const navigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        // always bypass prefixing for delta navigations
        return nativeNavigate(to);
      }

      if (loading) {
        // defer until loading finishes
        pendingNav.current = { to, options };
        return;
      }

      applyPrefixAndNavigate(to, options);
    },
    [loading, nativeNavigate, applyPrefixAndNavigate],
  );

  // whenever loading flips to false, flush any pending navigation
  useEffect(() => {
    if (!loading && pendingNav.current) {
      const { to, options } = pendingNav.current;
      // clear before calling so we don't loop
      pendingNav.current = null;
      applyPrefixAndNavigate(to, options);
    }
  }, [loading, applyPrefixAndNavigate]);

  return {
    loading,
    navigate,
    partnerPrefix: selectedPartnerFriendlyId,
    pathnameWithoutPrefix: trimDuplicatedSlashes(
      pathname.replace(selectedPartnerFriendlyId ?? '', ''),
    ),
  };
};

export function trimDuplicatedSlashes(url: string) {
  return url.replace(/\/+/g, '/');
}
